import type { ReactNode } from "react"
import { Navigate, useLocation } from "react-router-dom"
import { useAuth } from "@edemics/contexts/auth-provider"

/**
 * Route-level auth gate. Wraps every authenticated route.
 *
 * - `isLoading` true (rehydrating from localStorage) → render nothing so the
 *   sidebar doesn't flash the wrong logged-in state.
 * - Unauthenticated → redirect to /login with a `?redirect=` back-hop so the
 *   user lands where they intended after signing in. /login lives in the
 *   landing app; tenant-app just hands off.
 * - Authenticated tenant admin whose onboarding is not complete → send them
 *   to /onboarding. Super-admin provisions a tenant, the admin logs in for
 *   the first time, but no company-setup pass has run → landing on
 *   /dashboard would put them in an unusable app with no school details.
 *   We route them into the setup wizard first. Only admins get the forced
 *   hop; other roles (teacher/parent/student) don't own the setup flow.
 *   The onboarding surface itself is exempt so this doesn't loop.
 * - Otherwise → render children.
 */
export function AuthGuard({ children }: { children: ReactNode }) {
  const { user, isAuthenticated, isLoading } = useAuth()
  const location = useLocation()

  if (isLoading) return null

  if (!isAuthenticated) {
    const redirect = encodeURIComponent(location.pathname + location.search)
    return <Navigate to={`/login?redirect=${redirect}`} replace />
  }

  const alreadyOnOnboarding = location.pathname.startsWith("/onboarding")
  const alreadyOnChangePassword = location.pathname === "/change-password"
  const isTenantAdmin =
    user?.kind === "tenant" && (user.role === "admin" || user.role === "Admin")
  if (isTenantAdmin && user.onboardingCompleted === false && !alreadyOnOnboarding) {
    return <Navigate to="/onboarding" replace />
  }

  // Force-password-change gate (2026-08-28). Applies to EVERY tenant
  // role, not just admins — an admin can flip the flag on any user via
  // /tenant-settings/force-password-change and we route them to
  // /change-password until they reset. The onboarding gate above takes
  // precedence so a fresh admin still lands on setup first.
  if (
    user?.kind === "tenant" &&
    user.mustChangePassword === true &&
    !alreadyOnChangePassword
  ) {
    return <Navigate to="/change-password" replace />
  }

  return <>{children}</>
}
